import { useEffect, useState } from 'react';
import { adminReportsAPI } from '../../lib/admin-api';

type ReportStatus = 'pending' | 'reviewing' | 'resolved' | 'dismissed';

interface Report {
  id: number;
  reporter_id: number;
  reported_user_id: number;
  report_type: string;
  description: string;
  status: ReportStatus;
  created_at: string;
  resolved_at?: string;
  resolved_by?: number;
  reporter_username?: string;
  reported_username?: string;
}

export default function AdminReportsPage() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ReportStatus | 'all'>('pending');
  const [processing, setProcessing] = useState<number | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const data = await adminReportsAPI.getReports({
        status: filter === 'all' ? undefined : filter,
        limit: 100,
      });
      setReports(data.reports);
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (reportId: number, action: 'resolve' | 'dismiss') => {
    setProcessing(reportId);
    try {
      await adminReportsAPI.resolveReport(reportId, {
        action,
        resolution_note: resolutionNote || undefined,
      });
      setResolutionNote('');
      setSelectedReport(null);
      await loadReports();
    } catch (error) {
      console.error('Failed to resolve report:', error);
      alert('Failed to process report');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status: ReportStatus) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning">Pending</span>;
      case 'reviewing':
        return <span className="badge badge-info">Reviewing</span>;
      case 'resolved':
        return <span className="badge badge-success">Resolved</span>;
      case 'dismissed':
        return <span className="badge badge-ghost">Dismissed</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const getReportTypeIcon = (type: string) => {
    switch (type) {
      case 'spam':
        return '📧';
      case 'scam':
        return '⚠️';
      case 'harassment':
        return '🚫';
      case 'fake_profile':
        return '🎭';
      case 'inappropriate_content':
        return '🔞';
      default:
        return '📝';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Reports Management</h1>
        <div className="flex gap-2">
          <button
            className={`btn btn-sm ${filter === 'all' ? 'btn-active' : 'btn-ghost'}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`btn btn-sm ${filter === 'pending' ? 'btn-active' : 'btn-ghost'}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`btn btn-sm ${filter === 'reviewing' ? 'btn-active' : 'btn-ghost'}`}
            onClick={() => setFilter('reviewing')}
          >
            Reviewing
          </button>
          <button
            className={`btn btn-sm ${filter === 'resolved' ? 'btn-active' : 'btn-ghost'}`}
            onClick={() => setFilter('resolved')}
          >
            Resolved
          </button>
          <button
            className={`btn btn-sm ${filter === 'dismissed' ? 'btn-active' : 'btn-ghost'}`}
            onClick={() => setFilter('dismissed')}
          >
            Dismissed
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-base-100 rounded-box shadow">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Reporter</th>
              <th>Reported User</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-base-content/60">
                  No reports found
                </td>
              </tr>
            ) : (
              reports.map((report) => (
                <tr key={report.id} className="hover">
                  <td>{report.id}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span>{getReportTypeIcon(report.report_type)}</span>
                      <span className="text-sm">{report.report_type}</span>
                    </div>
                  </td>
                  <td>
                    <div className="font-semibold">{report.reporter_username || report.reporter_id}</div>
                    <div className="text-sm text-base-content/60">ID: {report.reporter_id}</div>
                  </td>
                  <td>
                    <div className="font-semibold text-error">
                      {report.reported_username || report.reported_user_id}
                    </div>
                    <div className="text-sm text-base-content/60">ID: {report.reported_user_id}</div>
                  </td>
                  <td>
                    <div className="max-w-xs truncate">{report.description}</div>
                  </td>
                  <td>{getStatusBadge(report.status)}</td>
                  <td>
                    <div className="text-sm">
                      {new Date(report.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {new Date(report.created_at).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>
                    {report.status === 'pending' || report.status === 'reviewing' ? (
                      <div className="flex gap-2">
                        <button
                          className="btn btn-xs btn-success"
                          onClick={() => setSelectedReport(report)}
                          disabled={processing === report.id}
                        >
                          Review
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-base-content/60">
                        {report.resolved_at
                          ? new Date(report.resolved_at).toLocaleDateString()
                          : '-'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Resolution Modal */}
      {selectedReport && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg mb-4">Review Report #{selectedReport.id}</h3>

            <div className="space-y-4">
              <div>
                <label className="label">
                  <span className="label-text font-semibold">Type</span>
                </label>
                <div className="flex items-center gap-2">
                  <span>{getReportTypeIcon(selectedReport.report_type)}</span>
                  <span>{selectedReport.report_type}</span>
                </div>
              </div>

              <div>
                <label className="label">
                  <span className="label-text font-semibold">Reporter</span>
                </label>
                <div>
                  {selectedReport.reporter_username || selectedReport.reporter_id}
                  <span className="text-sm text-base-content/60 ml-2">
                    (ID: {selectedReport.reporter_id})
                  </span>
                </div>
              </div>

              <div>
                <label className="label">
                  <span className="label-text font-semibold">Reported User</span>
                </label>
                <div className="text-error">
                  {selectedReport.reported_username || selectedReport.reported_user_id}
                  <span className="text-sm text-base-content/60 ml-2">
                    (ID: {selectedReport.reported_user_id})
                  </span>
                </div>
              </div>

              <div>
                <label className="label">
                  <span className="label-text font-semibold">Description</span>
                </label>
                <div className="bg-base-200 p-4 rounded-box">
                  {selectedReport.description}
                </div>
              </div>

              <div>
                <label className="label">
                  <span className="label-text font-semibold">Resolution Note (Optional)</span>
                </label>
                <textarea
                  className="textarea textarea-bordered w-full"
                  placeholder="Add a note about your decision..."
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setSelectedReport(null);
                  setResolutionNote('');
                }}
                disabled={processing === selectedReport.id}
              >
                Cancel
              </button>
              <button
                className="btn btn-error"
                onClick={() => handleResolve(selectedReport.id, 'dismiss')}
                disabled={processing === selectedReport.id}
              >
                {processing === selectedReport.id ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  'Dismiss'
                )}
              </button>
              <button
                className="btn btn-success"
                onClick={() => handleResolve(selectedReport.id, 'resolve')}
                disabled={processing === selectedReport.id}
              >
                {processing === selectedReport.id ? (
                  <span className="loading loading-spinner loading-sm"></span>
                ) : (
                  'Resolve & Take Action'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
